import AttachFileRoundedIcon from '@mui/icons-material/AttachFileRounded';
import AttachMoneyRoundedIcon from '@mui/icons-material/AttachMoneyRounded';
import ContactMailIcon from '@mui/icons-material/ContactMail';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import MarkEmailReadRoundedIcon from '@mui/icons-material/MarkEmailReadRounded';
import MoreTimeRoundedIcon from '@mui/icons-material/MoreTimeRounded';
import { Box, Grid, IconButton, Stack, Typography } from '@mui/material';
import dayjs from 'dayjs';
import { MissingPhoto } from 'Interfaces/IGlobalTypeConfig';
import { IProductViewModel } from 'Interfaces/IProductConfig';
import {
  ITicketBillingViewModel,
  ITicketPostViewModel,
} from 'Interfaces/ITicketConfig';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { EditTicketBillingDrawer } from './Drawers/EditTicketBillingDrawer';
import { SingleProductView } from './SingleProductView';

interface SingleBillingTicketViewProps {
  ticket: ITicketBillingViewModel;
  onTicketUpdate?: (updatedTicket: ITicketBillingViewModel) => void; // Add callback for updates
}

const SingleBillingTicketView: React.FC<SingleBillingTicketViewProps> = ({
  ticket,
  onTicketUpdate,
}) => {
  const [expanded, setExpanded] = useState(false);
  const [localTicket, setLocalTicket] = useState(ticket); // Local state for editing
  console.log('TK VIEW ', ticket);
  const { t } = useTranslation();

  // Update local state when prop changes
  useEffect(() => {
    setLocalTicket(ticket);
  }, [ticket]);

  const handleExpandClick = () => {
    setExpanded(!expanded);
  };

  const handleSave = (editedTicket: ITicketBillingViewModel) => {
    setLocalTicket(editedTicket); // Update local state
    onTicketUpdate?.(editedTicket); // Notify parent component
  };

  const handleEditClick = () => {
    // Handle edit click logic here
    // open a drawer or modal for editing
    //<EditModal ticket={ticket} onClose={() => {}} />
  };

  console.log('consumableProducts', ticket.consumableProducts);
  // Calculate total billing value based on fixedPrice, maxPrice, and ticketPosts

  let totalBillingValue =
    localTicket.fixedPrice >= 0
      ? localTicket.fixedPrice
      : localTicket.maxPrice >= 0
      ? Math.min(
          localTicket.maxPrice,
          localTicket.ticketPosts.reduce(
            (sum: number, post: ITicketPostViewModel) =>
              sum + post.totalBillingValue,
            0
          )
        )
      : localTicket.ticketPosts.reduce(
          (sum: number, post: ITicketPostViewModel) =>
            sum + post.totalBillingValue,
          0
        );

  totalBillingValue += localTicket.consumableProducts.reduce(
    (sum: number, product: IProductViewModel) =>
      product.isActive ? sum + product.retailPrice : sum,
    0
  );

  return (
    <Grid
      container
      alignItems='center'
      sx={{ mb: 1, padding: 1, border: '1px solid lightgray', borderRadius: 3 }}
    >
      {/* Ticket Icon */}
      <Grid
        item
        xs={1}
        sx={{ width: 48, height: 48, backgroundColor: 'primary.light' }}
      >
        <img
          src={MissingPhoto.BILLING_SUPPORT_URL}
          style={{
            marginLeft: 5,
            width: 48,
            height: 48,
            borderRadius: 3,
            objectFit: 'scale-down',
            boxShadow: '0px 0px 5px 0px rgba(0,0,0,0.50)',
          }}
          alt={'logo'}
        />
      </Grid>

      {/* Ticket Number and Calculated Total */}
      <Grid item sx={{ flexGrow: 1 }}>
        <Box display='flex' alignItems='center'>
          <Typography variant='body1' mr='5px'>
            {localTicket.title}
          </Typography>
        </Box>

        <Stack direction='row' spacing={0} alignItems='center'>
          <Typography
            variant='body2'
            sx={{ color: 'primary.contrastText', fontWeight: 'normal' }}
          >
            TicketNo: {localTicket.ticketNo}
          </Typography>
          <Typography
            variant='body2'
            sx={{
              flexGrow: 1,
              textAlign: 'center',
              color: 'primary.contrastText',
              fontWeight: 'normal',
            }}
          ></Typography>
          <Typography
            variant='body2'
            sx={{
              color: 'primary.contrastText',
              fontWeight: 'normal',
              width: '10%',
            }}
          >
            Total:{' '}
          </Typography>
          <Typography
            variant='body2'
            sx={{ color: 'secondary.text', fontWeight: 'bold', width: '20%' }}
          >
            {totalBillingValue.toLocaleString(undefined, {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}
          </Typography>
        </Stack>
      </Grid>

      {/* Expand Button */}
      <Grid item sx={{ mr: 1 }}>
        <IconButton onClick={handleExpandClick} aria-expanded={expanded}>
          {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </IconButton>
      </Grid>
      <Grid item sx={{ mr: 1 }}>
        <EditTicketBillingDrawer
          buttonIcon={<EditRoundedIcon />}
          buttonContext='Edit Ticket'
          ticket={localTicket} // Pass the local state
          onSave={handleSave} // Pass the save handler
          totalBillingValue={totalBillingValue}
        />
      </Grid>
      {/* Expanded Ticket Posts */}
      {expanded && (
        <Grid item xs={12} sx={{ p: 1 }}>
          {localTicket.fixedPrice > 0 ? (
            <Grid
              container
              key={'fixedPrice'}
              direction='column'
              alignItems='flex-start'
              sx={{
                mb: 1,
                p: 1,
                backgroundColor: 'rgba(0, 0, 0, 0.1)',
                borderRadius: '8px',
                boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)',
              }}
            >
              {/* Date and Icons Row */}
              <Box display='flex' alignItems='center' gap={1} sx={{ mb: 1 }}>
                <Typography
                  sx={{
                    fontSize: 12,
                    letterSpacing: 1.5,
                    fontWeight: 'medium',
                    color: 'text.secondary',
                  }}
                >
                  {new Date().toLocaleDateString()}{' '}
                  {/* .format('DD-MM-YYYY HH:mm')} */}
                </Typography>
              </Box>

              {/* Author Image, Message, and Billing Value */}
              <Box
                display='flex'
                alignItems='center'
                gap={2}
                sx={{ width: '100%' }}
              >
                {/* Author Image */}
                <img
                  src={MissingPhoto.EMPLOYEE_URL}
                  alt='Author'
                  style={{
                    width: 32,
                    height: 32,
                    borderRadius: '50%',
                    objectFit: 'cover',
                  }}
                />
                <Grid item xs={8}>
                  {/* Message */}
                  <Typography
                    variant='body2'
                    sx={{
                      display: '-webkit-box',
                      WebkitLineClamp: 3, // Maks 3 linjer
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis', // Legger til "..." for indikasjon
                    }}
                  >
                    {t('Billing.billingView.fixedPrice')}
                  </Typography>
                </Grid>
                {/* Billing Value */}
                <Typography
                  sx={{
                    fontWeight: 'bold',
                    color: 'secondary.text',
                    fontSize: 14,
                  }}
                >
                  {localTicket.fixedPrice.toLocaleString(undefined, {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
                </Typography>
              </Box>
            </Grid>
          ) : (
            localTicket.ticketPosts.map(
              (post: ITicketPostViewModel, index: number) => {
                const dateTime = dayjs(post.startTime);
                const startTime = dayjs(post.startTime);
                const endTime = dayjs(post.endTime);
                const duration = endTime.diff(startTime, 'minute');
                return (
                  <Grid
                    container
                    key={index}
                    direction='column'
                    alignItems='flex-start'
                    sx={{
                      mb: 1,
                      p: 1,
                      backgroundColor: 'rgba(0, 0, 0, 0.1)',
                      borderRadius: '8px',
                      boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)',
                    }}
                  >
                    {/* Date and Icons Row */}
                    <Box
                      display='flex'
                      alignItems='center'
                      gap={1}
                      sx={{ mb: 1 }}
                    >
                      <Typography
                        sx={{
                          fontSize: 12,
                          letterSpacing: 1.5,
                          fontWeight: 'medium',
                          color: 'text.secondary',
                        }}
                      >
                        {dateTime.format('DD-MM-YYYY HH:mm')}
                      </Typography>

                      {/* Conditional Icons */}
                      {post.isEmailSent && (
                        <IconButton aria-label='isEmailSent' size='small'>
                          <MarkEmailReadRoundedIcon
                            sx={{ fontSize: 18, color: 'success.text' }}
                          />
                        </IconButton>
                      )}
                      {post.isCCSent && (
                        <IconButton aria-label='isCCSent' size='small'>
                          <ContactMailIcon
                            sx={{ fontSize: 18, color: 'success.text' }}
                          />
                        </IconButton>
                      )}
                      {post.isOvertime && (
                        <IconButton aria-label='overtime' size='small'>
                          <MoreTimeRoundedIcon
                            sx={{ fontSize: 18, color: 'success.text' }}
                          />
                        </IconButton>
                      )}
                      {post.isBillable && (
                        <IconButton aria-label='isBillable' size='small'>
                          <AttachMoneyRoundedIcon
                            sx={{ fontSize: 18, color: 'success.text' }}
                          />
                        </IconButton>
                      )}
                      {post?.attachments && post.attachments.length > 0 && (
                        <IconButton aria-label='attachment' size='small'>
                          <AttachFileRoundedIcon
                            sx={{ fontSize: 18, color: 'info.main' }}
                          />
                        </IconButton>
                      )}

                      <Typography
                        sx={{
                          fontSize: 12,
                          letterSpacing: 1.5,
                          fontWeight: 'medium',
                          color: 'text.secondary',
                        }}
                      >
                        {dayjs(post.startTime).format('HH:mm')} -{' '}
                        {dayjs(post.endTime).format('HH:mm')} : {duration} min
                      </Typography>
                    </Box>

                    {/* Author Image, Message, and Billing Value */}
                    <Box
                      display='flex'
                      alignItems='center'
                      gap={2}
                      sx={{ width: '100%' }}
                    >
                      {/* Author Image */}
                      <img
                        src={post.author?.url || MissingPhoto.EMPLOYEE_URL}
                        alt='Author'
                        style={{
                          width: 32,
                          height: 32,
                          borderRadius: '50%',
                          objectFit: 'cover',
                        }}
                      />
                      <Grid item xs={8}>
                        {/* Message */}
                        <Typography
                          variant='body2'
                          sx={{
                            display: '-webkit-box',
                            WebkitLineClamp: 3, // Maks 3 linjer
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis', // Legger til "..." for indikasjon
                          }}
                        >
                          {post.message}
                        </Typography>
                      </Grid>
                      {/* Billing Value */}
                      <Typography
                        sx={{
                          fontWeight: 'bold',
                          color: 'secondary.text',
                          fontSize: 14,
                        }}
                      >
                        {post.totalBillingValue.toLocaleString(undefined, {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                      </Typography>
                    </Box>
                  </Grid>
                );
              }
            )
          )}
          {localTicket.consumableProducts
            .filter((product: IProductViewModel) => product.isActive)
            .map((post: IProductViewModel, index: number) => (
              <Box
                key={index}
                sx={{
                  transform: `scale(${0.8})`,
                  transformOrigin: 'left',
                  m: 0,
                  p: 0,
                }}
              >
                <SingleProductView product={post} />
              </Box>
            ))}
        </Grid>
      )}
    </Grid>
  );
};

export default SingleBillingTicketView;
